import React from 'react';
import PropTypes from 'prop-types';
import { FaSquare, FaPlus } from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig';
import { Modal } from './common';
import './Modal.css';
import './FichaITSModal.css';

const FichaITSModal = ({ employee, onClose, onConfirm, onUpdate }) => {
  const [checklist, setChecklist] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [editingDate, setEditingDate] = React.useState(false);
  const [dateLoading, setDateLoading] = React.useState(false);
  const [adjuntos, setAdjuntos] = React.useState([]);
  const [loadingAdjuntos, setLoadingAdjuntos] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);

  React.useEffect(() => {
    if (employee) {
      // Solo incluir en el checklist los items que tienen valor 1
      const activeRequirements = {
        ...(employee.necesita_laptop === 1 && { laptop: false }),
        ...(employee.necesita_aio === 1 && { aio: false }),
        ...(employee.necesita_profit_adm === 1 && { profit_administrativo: false }),
        ...(employee.necesita_profit_com === 1 && { profit_contable: false }),
        ...(employee.necesita_profit_nom === 1 && { profit_nomina: false }),
        ...(employee.necesita_usuario_red === 1 && { usuario_red: false }),
        ...(employee.necesita_correo === 1 && { correo: false }),
        ...(employee.necesita_huella === 1 && { huella: false }),
        ...(employee.necesita_telefono === 1 && { telefono: false }),
        ...(employee.necesita_tablet === 1 && { tablet: false }),
        ...(employee.necesita_centro_sura === 1 && { centro_sura: false }),
        ...(employee.necesita_cima === 1 && { cima: false }),
        ...(employee.necesita_soluciones_ti === 1 && { soluciones_ti: false })
      };

      setChecklist(activeRequirements);

      // Cargar adjuntos
      const fetchAdjuntos = async () => {
        try {
          setLoadingAdjuntos(true);
          const response = await axiosInstance.get(`/api/empleados/${employee.id}/adjuntos`);
          setAdjuntos(response.data);
        } catch (error) {
          console.error('Error cargando adjuntos:', error);
        } finally {
          setLoadingAdjuntos(false);
        }
      };

      fetchAdjuntos();
    }
  }, [employee]);

  const handleCheckToggle = async (key) => {
    if (loading) return;

    setLoading(true);

    try {
      const fieldMapping = {
        laptop: 'necesita_laptop',
        aio: 'necesita_aio',
        profit_administrativo: 'necesita_profit_adm',
        profit_contable: 'necesita_profit_com',
        profit_nomina: 'necesita_profit_nom',
        usuario_red: 'necesita_usuario_red',
        correo: 'necesita_correo',
        huella: 'necesita_huella',
        telefono: 'necesita_telefono',
        tablet: 'necesita_tablet',
        centro_sura: 'necesita_centro_sura',
        cima: 'necesita_cima',
        soluciones_ti: 'necesita_soluciones_ti'
      };

      // Primero actualizar el estado local para feedback inmediato
      setChecklist(prev => {
        const newChecklist = { ...prev };
        delete newChecklist[key];
        return newChecklist;
      });

      // Luego actualizar la BD
      const response = await axiosInstance.patch(`/api/empleados/${employee.id}`, {
        [fieldMapping[key]]: 0
      });

      if (!response.data.message) {
        throw new Error('No se pudo actualizar el requisito');
      }

      // Si la actualización fue exitosa, actualizar el componente padre
      if (onUpdate) {
        await onUpdate();
      }

    } catch (error) {
      console.error('Error en handleCheckToggle:', error);
      // Revertir el cambio local si hubo error
      setChecklist(prev => {
        return {
          ...prev,
          [key]: false
        };
      });
      alert('Error al actualizar el requisito');
    } finally {
      setLoading(false);
    }
  };

  const isAllChecked = () => {
    // Si no hay requisitos pendientes, retornar true
    if (Object.keys(checklist).length === 0) return true;
    return Object.values(checklist).every(value => value === true);
  };

  // Agregar manejador para clicks fuera del modal
  const handleOverlayClick = (e) => {
    if (e.target.className === 'its-modal-overlay') {
      onClose();
    }
  };

  // Función auxiliar para formatear la fecha correctamente
  const formatDate = (dateString) => {
    if (!dateString) return 'No especificada';
    // Parsear la fecha manteniendo la zona horaria
    const date = new Date(dateString);
    // Ajustar a la zona horaria local
    const localDate = new Date(date.getTime() + (date.getTimezoneOffset() * 60000));
    return localDate.toLocaleDateString();
  };

  const handleDateChange = async (e) => {
    try {
      setDateLoading(true);
      const newDate = e.target.value;
      
      const response = await axiosInstance.patch(`/api/empleados/${employee.id}`, {
        fecha_ingreso: newDate  // Enviar la fecha directamente sin ajustes
      });

      if (!response.data.message) {
        throw new Error('No se pudo actualizar la fecha');
      }

      if (onUpdate) {
        await onUpdate();
      }
      
      setEditingDate(false);
    } catch (error) {
      console.error('Error actualizando fecha:', error);
      alert('Error al actualizar la fecha');
    } finally {
      setDateLoading(false);
    }
  };

  // Agregar función para manejar la subida de archivos
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar que sea una imagen o PDF
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      alert('Solo se permiten archivos de imagen o PDF');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('ficha', employee.ficha);

      const response = await axiosInstance.post(
        `/api/empleados/${employee.id}/adjuntos`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Recargar los adjuntos
      const adjuntosResponse = await axiosInstance.get(`/api/empleados/${employee.id}/adjuntos`);
      setAdjuntos(adjuntosResponse.data);

    } catch (error) {
      console.error('Error subiendo archivo:', error);
      alert('Error al subir el archivo');
    } finally {
      setUploading(false);
    }
  };

  if (!employee) return null;

  // Verificar si hay requisitos pendientes
  const hasRequirements = Object.keys(checklist).length > 0;

  return (
    <Modal isOpen={!!employee} onClose={onClose} size="xl">
      <Modal.Header onClose={onClose}>Ficha TI</Modal.Header>
      <Modal.Body className="p-0">
        <div className="its-modal__body p-6">
          <div className="its-modal__main-content">
            <div className="its-modal__employee-info">
              <h3 className="its-modal__section-title">Datos del Empleado</h3>
              <p className="its-modal__field">
                <span className="its-modal__field-label">Sede:</span> {employee.sede}
              </p>
              <p className="its-modal__field">
                <span className="its-modal__field-label">Nombre:</span> {employee.nombre}
              </p>
              <p className="its-modal__field">
                <span className="its-modal__field-label">Cedula:</span> {employee.cedula}
              </p>
              <p className="its-modal__field">
                <span className="its-modal__field-label">Ficha:</span> {employee.ficha}
              </p>
              <p className="its-modal__field">
                <span className="its-modal__field-label">Area:</span> {employee.area}
              </p>
              <p className="its-modal__field">
                <span className="its-modal__field-label">Cargo:</span> {employee.cargo}
              </p>
              <div className="its-modal__field">
                <span className="its-modal__field-label">Fecha de Ingreso: </span>
                {editingDate ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="date"
                      defaultValue={employee.fecha_ingreso ? employee.fecha_ingreso.split('T')[0] : ''}
                      onChange={handleDateChange}
                      disabled={dateLoading}
                      style={{ padding: '2px 5px' }}
                    />
                    {dateLoading && <span>...</span>}
                  </span>
                ) : (
                  <span>
                    {' '}{formatDate(employee.fecha_ingreso)}
                    <button
                      onClick={() => setEditingDate(true)}
                      className="its-modal__edit-button"
                      style={{
                        marginLeft: '10px',
                        padding: '2px 8px',
                        background: 'none',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Editar
                    </button>
                  </span>
                )}
              </div>
            </div>

            <div className="its-modal__requirements">
              {hasRequirements ? (
                <>
                  <h3 className="its-modal__section-title">Requisitos por Aprobar</h3>
                  {Object.entries(checklist).map(([key, checked]) => (
                    <div 
                      key={key} 
                      className={`its-modal__checklist-item ${
                        loading ? 'its-modal__checklist-item--disabled' : ''
                      }`}
                      onClick={() => !loading && handleCheckToggle(key)}
                    >
                      {loading ? (
                        <div className="its-modal__loading-indicator">...</div>
                      ) : (
                        <FaSquare className="its-modal__checkbox-icon" />
                      )}
                      <span>
                        {key === 'laptop' && 'Laptop'}
                        {key === 'aio' && 'All-in-One'}
                        {key === 'usuario_red' && 'Usuario de Red'}
                        {key === 'correo' && 'Correo Electrónico'}
                        {key === 'huella' && 'Acceso Biométrico'}
                        {key === 'profit_administrativo' && 'Profit - Módulo Administrativo'}
                        {key === 'profit_contable' && 'Profit - Módulo Contable'}
                        {key === 'profit_nomina' && 'SPI'}
                        {key === 'telefono' && 'Teléfono móvil'}
                        {key === 'tablet' && 'Tablet'}
                        {key === 'centro_sura' && 'Usuario Centro Sura'}
                        {key === 'cima' && 'Usuario Cima'}
                        {key === 'soluciones_ti' && 'Usuario Soluciones TI'}
                      </span>
                    </div>
                  ))}
                </>
              ) : (
                <div className="its-modal__no-requirements">
                  <p>No hay requisitos pendientes por aprobar.</p>
                </div>
              )}
            </div>
          </div>

          <div className="its-modal__adjuntos">
            <h3 className="its-modal__section-title">Documentos Adjuntos</h3>
            {loadingAdjuntos ? (
              <div className="its-modal__loading">Cargando documentos...</div>
            ) : (
              <>
                <div className="its-modal__adjuntos-list">
                  {adjuntos.map((adjunto, index) => (
                    <div key={index} className="its-modal__adjunto-item">
                      <a 
                        href={`/api/uploads/empleados/${employee.ficha}/${adjunto.nombre}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="its-modal__thumbnail-link"
                        title={adjunto.nombre}
                      >
                        {adjunto.nombre.toLowerCase().endsWith('.pdf') ? (
                          <div className="its-modal__pdf-preview">
                            <div className="pdf-icon">PDF</div>
                            <span className="pdf-filename">{adjunto.nombre}</span>
                          </div>
                        ) : (
                          <img 
                            src={`/api/uploads/empleados/${employee.ficha}/${adjunto.nombre}`}
                            alt={adjunto.nombre}
                            className="its-modal__thumbnail"
                          />
                        )}
                      </a>
                    </div>
                  ))}
                </div>
                
                <div className="its-modal__upload-section">
                  <input
                    type="file"
                    id="file-upload"
                    accept="image/*,application/pdf"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                  <label 
                    htmlFor="file-upload"
                    className="its-modal__upload-button"
                  >
                    {uploading ? (
                      <span>Subiendo...</span>
                    ) : (
                      <>
                        <FaPlus /> Añadir Adjunto
                      </>
                    )}
                  </label>
                </div>
              </>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          onClick={onClose}
          className="btn btn-secondary"
        >
          Cancelar
        </button>
        <button
          onClick={onConfirm}
          className="btn btn-primary"
          disabled={!isAllChecked()}
        >
          Aprobar
        </button>
      </Modal.Footer>
    </Modal>
  );
};

FichaITSModal.propTypes = {
  employee: PropTypes.shape({
    id: PropTypes.number,
    nombre: PropTypes.string,
    ficha: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cargo: PropTypes.string,
    departamento: PropTypes.string,
    fecha_ingreso: PropTypes.string,
    necesita_aio: PropTypes.number,
    necesita_profit_adm: PropTypes.number,
    necesita_profit_com: PropTypes.number,
    necesita_profit_nom: PropTypes.number,
    necesita_usuario_red: PropTypes.number,
    necesita_correo: PropTypes.number,
    necesita_huella: PropTypes.number,
    necesita_telefono: PropTypes.number,
    necesita_tablet: PropTypes.number,
    necesita_centro_sura: PropTypes.number,
    necesita_cima: PropTypes.number,
    necesita_soluciones_ti: PropTypes.number
  }),
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onUpdate: PropTypes.func
};

export default FichaITSModal; 